export type SubscriptionTierKey = 'trial' | 'basic' | 'standard' | 'custom' | 'expired'

/** Tiers that can be purchased or selected in Stripe (excludes pre-subscription trial). */
export type SelectablePlanKey = 'basic' | 'standard' | 'custom'

/** Stripe display fields per paid catalog tier (`GET /tierlimits` uses the same Price IDs as checkout). */
export interface StripePaidTierPriceDisplay {
  unitAmount: number
  currency: string
  interval: string | null
  intervalCount: number | null
}

export interface TierStripePrices {
  basic: StripePaidTierPriceDisplay
  standard: StripePaidTierPriceDisplay
}

/** One tier row — caps from `/notefie/{env}/subscription/tier-config` via GET `/tierlimits` `limits`. */
export interface TierLimitsRow {
  users: number
  mqttAccounts: number
  endUserDevices: number
  trialPeriodDays?: number
}

/** `/notefie/{env}/subscription/tier-config` payload as `limits` (validated by `parseTierLimitsParameterValue` in the Lambda layer). */
export interface TierLimitsCatalog {
  trial: TierLimitsRow
  basic: TierLimitsRow
  standard: TierLimitsRow
  custom: null
  expired: TierLimitsRow
}

export interface TierDisplayLabels {
  name: string
  description: string
}

/** Presentation-only copy — no numeric caps (those live in TierLimitsCatalog). */
export const TIER_LABELS: Record<SubscriptionTierKey, TierDisplayLabels> = {
  trial: {
    name: 'Trial',
    description: 'Default access before you subscribe to a paid plan.'
  },
  basic: {
    name: 'Basic',
    description: 'For small teams getting started.'
  },
  standard: {
    name: 'Standard',
    description: 'For growing teams and customers.'
  },
  custom: {
    name: 'Custom',
    description: 'Tailored solution for your organisation needs.'
  },
  expired: {
    name: 'Expired',
    description: 'Subscription has ended. Renew to continue.'
  }
}

/** Tier keys shown as purchasable plans on Billing (excludes trial + expired). */
export const SELECTABLE_TIER_KEYS: SelectablePlanKey[] = ['basic', 'standard', 'custom']

/** Used after checkout / tier changes when we need Stripe + UI shape from catalog rows. */
export interface SubscriptionTierDefinition {
  key: SubscriptionTierKey
  name: string
  description: string
  /** Minor units — same Stripe `Price.unit_amount` used for subscriptions. Null for Custom. */
  paymentAmountMinor: number | null
  billingCurrency: string | null
  limits: {
    mqttAccounts: number | null
    users: number | null
    endUserDevices: number | null
  }
}

export function getTierDisplayName(tier: string | null | undefined): string {
  if (!tier) return 'Unknown'
  const lbl = TIER_LABELS[tier as SubscriptionTierKey]
  return lbl?.name ?? tier
}

/** Feature bullets for paid catalog tiers (MQTT / seats / devices from Parameter Store). */
export function formatPaidTierFeatureLines(entry: TierLimitsRow): string[] {
  return [
    `${entry.mqttAccounts} MQTT account${entry.mqttAccounts === 1 ? '' : 's'}`,
    `Up to ${entry.users} users in total`,
    `Up to ${entry.endUserDevices} end user devices`
  ]
}

export function selectableTierDefinitionsFromCatalog(
  catalog: TierLimitsCatalog,
  stripePrices: TierStripePrices
): Record<SelectablePlanKey, SubscriptionTierDefinition> {
  return {
    basic: {
      key: 'basic',
      name: TIER_LABELS.basic.name,
      description: TIER_LABELS.basic.description,
      paymentAmountMinor: stripePrices.basic.unitAmount,
      billingCurrency: stripePrices.basic.currency,
      limits: {
        mqttAccounts: catalog.basic.mqttAccounts,
        users: catalog.basic.users,
        endUserDevices: catalog.basic.endUserDevices
      }
    },
    standard: {
      key: 'standard',
      name: TIER_LABELS.standard.name,
      description: TIER_LABELS.standard.description,
      paymentAmountMinor: stripePrices.standard.unitAmount,
      billingCurrency: stripePrices.standard.currency,
      limits: {
        mqttAccounts: catalog.standard.mqttAccounts,
        users: catalog.standard.users,
        endUserDevices: catalog.standard.endUserDevices
      }
    },
    custom: {
      key: 'custom',
      name: TIER_LABELS.custom.name,
      description: TIER_LABELS.custom.description,
      paymentAmountMinor: null,
      billingCurrency: null,
      limits: { mqttAccounts: null, users: null, endUserDevices: null }
    }
  }
}

export function tierCatalogUserSeatCap(
  catalog: TierLimitsCatalog | null,
  tier: string | null | undefined
): number | null {
  if (!tier || !catalog) return null
  if (tier === 'custom') return null
  const row = catalog[tier as keyof TierLimitsCatalog]
  if (row === null || typeof row !== 'object') return null
  return typeof row.users === 'number' ? row.users : null
}
